/* eslint-disable complexity */
/**
 * Routing.
 *
 * Responsibility of this module is to replace Backbone classes History and Router
 * 
 * @module app/controllers/Routing
 */
const Events = {};
const eventSplitter = /\s+/;
let _listening;

/**
 * Iterates over the standard `event, callback` (as well as the fancy multiple
 * space-separated events `"change blur", callback` and jQuery-style event
 * maps `{event: callback}`).
 * 
 * @returns {Object}  events Object
 */
const eventsApi = (iteratee, events, name, callback, opts) => {
    let i = 0, names;
    if (name && typeof name === 'object') {
        if (callback !== undefined && 'context' in opts && opts.context === undefined) opts.context = callback;
        for (let [key, value] of Object.entries(name)) {
            events = eventsApi(iteratee, events, key, value, opts);
        }
    } else if (name && eventSplitter.test(name)) {
        for (names = name.split(eventSplitter); i < names.length; i++) {
            events = iteratee(events, names[i], callback, opts);
        }
    } else {
        events = iteratee(events, name, callback, opts);
    }
    return events;
};

/**
 * Bind an event to a `callback` function
 */
Events.on = function(name, callback, context) {
    this._events = eventsApi(onApi, this._events || {}, name, callback, {
        context: context,
        ctx: this,
        listening: _listening,
    });

    if (_listening) {
        const listeners = this._listeners || (this._listeners = {});
        listeners[_listening.id] = _listening;
        _listening.interop = false;
    }

    return this;
};

let idCounter = 0;

/**
 * Generates a unique id giving a prefix
 * 
 * @returns {String} unique id
 */
const uniqueId = (prefix) => {
    let id = ++idCounter + '';
    return prefix ? prefix + id : id;
};

/**
 * Inversion-of-control versions of `on`. Tell *this* object to listen to
 * an event in another object... keeping track of what it's listening to
 * for easier unbinding later.
 */
Events.listenTo = function(obj, name, callback) {
    if (!obj) return this;
    let id = obj._listenId || (obj._listenId = uniqueId('l'));
    let listeningTo = this._listeningTo || (this._listeningTo = {});
    let listening = _listening = listeningTo[id];

    if (!listening) {
        this._listenId || (this._listenId = uniqueId('l'));
        listening = _listening = listeningTo[id] = new Listening(this, obj);
    }

    const error = tryCatchOn(obj, name, callback, this);
    _listening = undefined;

    if (error) throw error;

    if (listening.interop) listening.on(name, callback);

    return this;
};

/**
 * The reducing API that adds a callback to the `events` object
 * 
 * @returns {Object} events
 */
const onApi = (events, name, callback, options) => {
    if (callback) {
        const handlers = events[name] || (events[name] = []);
        const context = options.context, ctx = options.ctx, listening = options.listening;
        if (listening) listening.count++;

        handlers.push({ callback: callback, context: context, ctx: context || ctx, listening: listening });
    }
    return events;
};

/**
 * An try-catch guarded #on function, to prevent poisoning the global
 * `_listening` variable.
 */
const tryCatchOn = (obj, name, callback, context) => {
    try {
        obj.on(name, callback, context);
    } catch (e) {
        return e;
    }
};

/**
 * Remove one or many callbacks
 */
Events.off = function(name, callback, context) {
    if (!this._events) return this;
    this._events = eventsApi(offApi, this._events, name, callback, {
        context: context,
        listeners: this._listeners,
    });

    return this;
};

/**
 * Tell this object to stop listening to either specific events ... or
 * to every object it's currently listening to
 */
Events.stopListening = function(obj, name, callback) {
    const listeningTo = this._listeningTo;
    if (!listeningTo) return this;

    let ids = obj ? [obj._listenId] : Object.keys(listeningTo);
    for (let i = 0; i < ids.length; i++) {
        const listening = listeningTo[ids[i]];

        if (!listening) break;

        listening.obj.off(name, callback, this);
        if (listening.interop) listening.off(name, callback);
    }
    if (Object.keys(listeningTo).length === 0 && listeningTo.constructor === Object) this._listeningTo = undefined;
    
    return this;
};

/**
 * The reducing API that removes a callback from the `events` object
 * 
 * @returns {Object} events
 */
const offApi = (events, name, callback, options) => {
    if (!events) return;

    const context = options.context, listeners = options.listeners;
    let i = 0, names;

    if (!name && !context && !callback) {
        for (names = Object.keys(listeners); i < names.length; i++) {
            listeners[names[i]].cleanup();
        }
        return;
    }

    names = name ? [name] : Object.keys(events);
    for (; i < names.length; i++) {
        name = names[i];
        const handlers = events[name];

        if (!handlers) break;

        let remaining = [];
        for (let j = 0; j < handlers.length; j++) {
            let handler = handlers[j];
            if (
                callback && callback !== handler.callback &&
            callback !== handler.callback._callback ||
              context && context !== handler.context
            ) {
                remaining.push(handler);
            } else {
                const listening = handler.listening;
                if (listening) listening.off(name, callback);
            }
        }

        if (remaining.length) {
            events[name] = remaining;
        } else {
            delete events[name];
        }
    }

    return events;
};

/**
 * Bind an event to only be triggered a single time
 */
Events.once = function(name, callback, context) {
    const events = eventsApi(onceMap, {}, name, callback, this.off.bind(this));
    if (typeof name === 'string' && context == null) callback = undefined;
    return this.on(events, callback, context);
};

/**
 * Inversion-of-control versions of `once
 */
Events.listenToOnce = function(obj, name, callback) {
    const events = eventsApi(onceMap, {}, name, callback, this.stopListening.bind(this, obj));
    return this.listenTo(obj, events);
};

/**
 * Returns a function that will be executed at most one time, no matter how often you call it
 *
 * @returns {void} 
 */
const doOnce = (f) => {
    let ran = false, memo;
    return function() {
        if (ran) return memo;
        ran = true;
        memo = f.apply(this, arguments);
        f = null;
        return memo;
    };
};

/**
 * Reduces the event callbacks into a map of `{event: onceWrapper}`
 */
const onceMap = (map, name, callback, offer) => {
    if (callback) {
        const once = map[name] = doOnce(function() {
            offer(name, once);
            callback.apply(this, arguments);
        });
        once._callback = callback;
    }
    return map;
};

/**
 * Trigger one or many events, firing all bound callbacks
 */
Events.trigger = function(name) {
    if (!this._events) return this;

    const length = Math.max(0, arguments.length - 1);
    const args = Array(length);
    for (let i = 0; i < length; i++) args[i] = arguments[i + 1];

    eventsApi(triggerApi, this._events, name, undefined, args);
    return this;
};

/**
 * Handles triggering the appropriate event callbacks
 */
const triggerApi = (objEvents, name, callback, args) => {
    if (objEvents) {
        const events = objEvents[name];
        let allEvents = objEvents.all;
        if (events && allEvents) allEvents = allEvents.slice();
        if (events) triggerEvents(events, args);
        if (allEvents) triggerEvents(allEvents, [name].concat(args));
    }
    return objEvents;
};

/**
 * optimized internal dispatch function for triggering events
 */
const triggerEvents = (events, args) => {
    let ev, i = -1, l = events.length, a1 = args[0], a2 = args[1], a3 = args[2];
    switch (args.length) {
    case 0: while (++i < l) (ev = events[i]).callback.call(ev.ctx); return;
    case 1: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1); return;
    case 2: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1, a2); return;
    case 3: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1, a2, a3); return;
    default: while (++i < l) (ev = events[i]).callback.apply(ev.ctx, args); return;
    }
};

/**
 * A listening class that tracks and cleans up memory bindings
 * when all callbacks have been offed.
 */
const Listening = function(listener, obj) {
    this.id = listener._listenId;
    this.listener = listener;
    this.obj = obj;
    this.interop = true;
    this.count = 0;
    this._events = undefined;
};

Listening.prototype.on = Events.on;

/**
 * Offs a callback (or several)
 */
Listening.prototype.off = function(name, callback) {
    let cleanup;
    if (this.interop) {
        this._events = eventsApi(offApi, this._events, name, callback, {
            context: undefined,
            listeners: undefined,
        });
        cleanup = !this._events;
    } else {
        this.count--;
        cleanup = this.count === 0;
    }
    if (cleanup) this.cleanup();
};

/**
 * Cleans up memory bindings between the listener and the listenee
 */
Listening.prototype.cleanup = () => {
    delete this.listener._listeningTo[this.obj._listenId];
    if (!this.interop) delete this.obj._listeners[this.id];
};

Events.bind   = Events.on;
Events.unbind = Events.off;

/**
 * Routers map faux-URLs to actions, and fire events when routes are
 * matched. Creating a new one sets its `routes` statically
 */
export const Router = function(options) {
    options || (options = {});
    this.preinitialize.apply(this, arguments);
    if (options.routes) this.routes = options.routes;
    this._bindRoutes();
    this.initialize.apply(this, arguments);
};

const optionalParam = /\((.*?)\)/g;
const namedParam    = /(\(\?)?:\w+/g;
const splatParam    = /\*\w+/g;
const escapeRegExp  = /[\-{}\[\]+?.,\\\^$|#\s]/g;

/**
 * Checks if arg is a RegExp
 * 
 * @returns {Boolean}
 */
const isRegExp = (route) => {
    let isValid = true;
    try {
        new RegExp(route);
    } catch(e) {
        isValid = false;
    }
    return isValid;
};

/**
 * If the value of the named property is a function then invoke it; otherwise, return it
 */
const getResult = (object, property) => {
    if (object == null) return undefined;
    const value = object[property];
    return (typeof value === 'function') ? value.call(object) : value;
};

Object.assign(Router.prototype, Events, {

    preinitialize: function(){},

    initialize: function(){},

    // Binds a single named route to a callback.
    route: function(route, name, callback) {
        if (isRegExp(route)) route = this._routeToRegExp(route);
        if (typeof name === 'function') {
            callback = name;
            name = '';
        }
        if (!callback) callback = this[name];
        const router = this;
        history.route(route, function(fragment) {
            const args = router._extractParameters(route, fragment);
            if (router.execute(callback, args, name) !== false) {
                router.trigger.apply(router, ['route:' + name].concat(args));
                router.trigger('route', name, args);
                history.trigger('route', router, name, args);
            }
        });
        return this;
    },

    // Execute a route handler with the provided parameters.
    execute: function(callback, args, name) {
        if (callback) {
            return callback.apply(this, args);
        }
        return undefined;
    },

    navigate: function(fragment, options) {
        history.navigate(fragment, options);
        return this;
    },

    _bindRoutes: function() {
        if (!this.routes) return;
        this.routes = getResult(this, 'routes');
        let route, routes = Object.keys(this.routes);
        while ((route = routes.pop()) != null) {
            this.route(route, this.routes[route]);
        }
    },

    _routeToRegExp: function(route) {
        route = route.replace(escapeRegExp, '\\$&')
            .replace(optionalParam, '(?:$1)?')
            .replace(namedParam, function(match, optional) {
                return optional ? match : '([^/?]+)';
            })
            .replace(splatParam, '([^?]*?)');
        return new RegExp('^' + route + '(?:\\?([\\s\\S]*))?$');
    },

    _extractParameters: function(route, fragment) {
        const params = route.exec(fragment).slice(1);
        return params.map(function(param, i) {
            if (i === params.length - 1) return param || null;
            return param ? decodeURIComponent(param) : null;
        });
    },

});

/**
 * Handles cross-browser history management.
*/
function History() {
    this.handlers = [];
    this.checkUrl = this.checkUrl.bind(this);

    if (typeof window !== 'undefined') {
        this.location = window.location;
        this.history = window.history;
    }
}

const routeStripper = /^[#\/]|\s+$/g;

const rootStripper = /^\/+|\/+$/g;

const pathStripper = /#.*$/;

History.started = false;

Object.assign(History.prototype, Events, {

    atRoot: function() {
        const path = this.location.pathname.replace(/[^\/]$/, '$&/');
        return path === this.root && !this.getSearch();
    },

    matchRoot: function() {
        const path = this.decodeFragment(this.location.pathname);
        const rootPath = path.slice(0, this.root.length - 1) + '/';
        return rootPath === this.root;
    },

    // Unicode characters in `location.pathname` are percent encoded so they're
    // decoded for comparison. `%25` should not be decoded since it may be part
    // of an encoded parameter.
    // Exception added to prevent this issue: https://github.com/jashkenas/backbone/pull/3434    
    decodeFragment: function(fragment) {
        return decodeURI(fragment.replace(/%25/g, '%2525'));
    },

    getSearch: function() {
        const match = this.location.href.replace(/#.*/, '').match(/\?.+/);
        return match ? match[0] : '';
    },

    getPath: function() {
        const path = this.decodeFragment(
            this.location.pathname + this.getSearch()
        ).slice(this.root.length - 1);
        return path.charAt(0) === '/' ? path.slice(1) : path;
    },

    getFragment: function(fragment) {
        if (fragment == null) {
            fragment = this.getPath();
        }
        return fragment.replace(routeStripper, '');
    },

    /**
     * Start the hash change handling, returning `true` if the current URL matches
     * an existing route, and `false` otherwise.
     */
    start: function(options) {
        if (History.started) throw new Error('history has already been started');
        History.started = true;

        this.options          = Object.assign({ root: '/' }, this.options, options);
        this.root             = this.options.root;
        this.fragment         = this.getFragment();

        this.root = ('/' + this.root + '/').replace(rootStripper, '/');

        const addEventListener = window.addEventListener || function(eventName, listener) {
            return window.attachEvent('on' + eventName, listener);
        };

        addEventListener('popstate', this.checkUrl, false);

        return this.loadUrl();
    },

    stop: function() {
        const removeEventListener = window.removeEventListener || function(eventName, listener) {
            return window.detachEvent('on' + eventName, listener);
        };

        removeEventListener('popstate', this.checkUrl, false);

        if (this.iframe) {
            document.body.removeChild(this.iframe);
            this.iframe = null;
        }

        if (this._checkUrlInterval) clearInterval(this._checkUrlInterval);
        History.started = false;
    },

    route: function(route, callback) {
        this.handlers.unshift({ route: route, callback: callback });
    },

    checkUrl: function(e) {
        let current = this.getFragment();

        if (current === this.fragment) return false;
        this.loadUrl();
    },

    loadUrl: function(fragment) {
        if (!this.matchRoot()) return false;
        fragment = this.getFragment(fragment);
        return this.handlers.some(function(handler) {
            if (handler.route.test(fragment)) {
                handler.callback(fragment);
                return true;
            }
        });
    },

    navigate: function(fragment, options) {
        if (!History.started) return false;
        if (!options || options === true) options = { trigger: !!options };

        fragment = this.getFragment(fragment || '');

        let rootPath = this.root;
        if (fragment === '' || fragment.charAt(0) === '?') {
            rootPath = rootPath.slice(0, -1) || '/';
        }
        const url = rootPath + fragment;

        fragment = fragment.replace(pathStripper, '');

        const decodedFragment = this.decodeFragment(fragment);

        if (this.fragment === decodedFragment) return;
        this.fragment = decodedFragment;

        this.history[options.replace ? 'replaceState' : 'pushState']({}, document.title, url);

        if (options.trigger) return this.loadUrl(fragment);
    },

});

// Creates the default history.
export const history = new History;
